import type { OrderStepPrimaryAction } from '../orderStepBar';
import type { OrderProcessStepKey } from '../orderProcessSteps';

/**
 * The step card of steps 1–3 (Szkic, Oferta, Potwierdzone) as in the first
 * mockup „Karta zamówienia ZK” (`stepc`, `checklist()`): eyebrow „Krok N z 8”,
 * title, help text and a checklist whose rows are ✓ done, ○ still to do or
 * i information. Pure rules only — the component renders them, the page
 * supplies the facts, and nothing here decides a permission or a business
 * rule: whether „Stwórz ofertę” waits is `orderStepBar.primaryDisabled`, the
 * checklist only says why. Statuses stay informational (WP3): a ○ row never
 * blocks anything the action bar does not already block.
 */
export type OrderGuideStepKey = 'DRAFT' | 'OFFER' | 'CONFIRMED';

export const ORDER_GUIDE_STEP_COUNT = 8;

export const isOrderGuideStep = (key: OrderProcessStepKey): key is OrderGuideStepKey =>
  key === 'DRAFT' || key === 'OFFER' || key === 'CONFIRMED';

export interface OrderGuideText {
  /** `orders:` translation key. */
  key: string;
  params?: Record<string, string | number>;
}

export type OrderGuideItemState = 'ok' | 'todo' | 'info';

export interface OrderGuideItem {
  id: string;
  state: OrderGuideItemState;
  text: OrderGuideText;
  /** Shown under a ○ row (mockup `hint`). */
  hint?: OrderGuideText;
  /** Opens „Ceny sprzedaży” (the only row action; shown with the step actions). */
  action?: 'prices';
}

const g = (key: string, params?: Record<string, string | number>): OrderGuideText => ({ key: `orders:process.guide.${key}`, ...(params ? { params } : {}) });

/** One line of the draft as the checklist sees it. */
export interface OrderGuideLineFacts {
  name: string;
  hasQuantity: boolean;
  hasPrice: boolean;
}

export interface OrderGuideDraftFacts {
  customerChosen: boolean;
  lines: OrderGuideLineFacts[];
  /** Whoever cannot read sale prices never has a „missing price”. */
  canViewSalePrices: boolean;
  /** „Oczekiwana dostawa” is filled in. */
  deliveryDateSet: boolean;
  /** „Ceny sprzedaży” works on a saved order and needs the right to edit prices. */
  canOpenPrices: boolean;
}

const names = (lines: OrderGuideLineFacts[]) => lines.map((line) => line.name).join(', ');

/** Szkic (mockup step 1): Klient wybrany, Pozycje zamówienia (N), Ceny sprzedaży uzupełnione, Termin dostawy ustalony. */
export function getDraftGuideItems(facts: OrderGuideDraftFacts): OrderGuideItem[] {
  const noQuantity = facts.lines.filter((line) => !line.hasQuantity);
  const noPrice = facts.canViewSalePrices ? facts.lines.filter((line) => !line.hasPrice) : [];
  const linesOk = facts.lines.length > 0 && noQuantity.length === 0;
  const items: OrderGuideItem[] = [
    {
      id: 'customer',
      state: facts.customerChosen ? 'ok' : 'todo',
      text: g('draft.customer'),
      hint: g('draft.customerHint'),
    },
    {
      id: 'lines',
      state: linesOk ? 'ok' : 'todo',
      text: noQuantity.length
        ? g('draft.linesNoQuantity', { count: facts.lines.length, lines: names(noQuantity) })
        : g('draft.lines', { count: facts.lines.length }),
      hint: g('draft.linesHint'),
    },
  ];
  if (facts.canViewSalePrices) {
    const pricesOk = facts.lines.length > 0 && noPrice.length === 0;
    items.push({
      id: 'prices',
      state: pricesOk ? 'ok' : 'todo',
      text: pricesOk || facts.lines.length === 0
        ? g('draft.prices')
        : g('draft.pricesMissing', { lines: names(noPrice) }),
      ...(!pricesOk && facts.canOpenPrices ? { action: 'prices' as const } : {}),
      hint: facts.canOpenPrices ? undefined : g('draft.pricesHint'),
    });
  }
  items.push({
    id: 'delivery',
    state: facts.deliveryDateSet ? 'ok' : 'todo',
    text: g('draft.delivery'),
    hint: g('draft.deliveryHint'),
  });
  return items;
}

/**
 * ZK paid in advance on a proforma (`paymentMode` PREPAYMENT, decision
 * 2026-10-27). `undefined` = the reader cannot see that fact (no sales
 * documents / customer payments), so the row is not a false „to do”.
 */
export interface OrderGuidePrepaymentFacts {
  /** „30 %”. */
  percent: string;
  /** Number of the proforma added to the ZK ('' = without a number), null = none yet. */
  proformaNumber: string | null | undefined;
  /** Formatted day the proforma was paid, null = not paid yet. */
  paidAt: string | null | undefined;
  /**
   * Number of the approved advance invoice ('' = without a number), null =
   * none yet (decision 2026-11-02: issued after the payment, it confirms the
   * money and recognizes nothing; the settlement invoice settles the ZK).
   */
  advanceNumber?: string | null | undefined;
}

/** Potwierdzone of a prepaid ZK: „Dodaj fakturę zaliczkową” → „Faktura zaliczkowa … zatwierdzona”. */
export function getConfirmedPrepaymentItems(prepayment: OrderGuidePrepaymentFacts | null | undefined): OrderGuideItem[] {
  if (!prepayment || prepayment.advanceNumber === undefined) return [];
  return [prepayment.advanceNumber !== null
    ? { id: 'advance', state: 'ok', text: prepayment.advanceNumber ? g('confirmed.advanceAdded', { number: prepayment.advanceNumber }) : g('confirmed.advanceAddedNoNumber'), hint: g('confirmed.advanceAddedHint') }
    : { id: 'advance', state: 'todo', text: g('confirmed.advance'), hint: g('confirmed.advanceHint') }];
}

/** Proforma rows of the Oferta step: wystawiona → opłacona → „Klient potwierdził”. Information only, nothing waits on them. */
function getPrepaymentGuideItems(prepayment: OrderGuidePrepaymentFacts): OrderGuideItem[] {
  const items: OrderGuideItem[] = [];
  if (prepayment.proformaNumber !== undefined) {
    items.push(prepayment.proformaNumber !== null
      ? { id: 'proforma', state: 'ok', text: prepayment.proformaNumber ? g('offer.proformaAdded', { number: prepayment.proformaNumber }) : g('offer.proformaAddedNoNumber') }
      : { id: 'proforma', state: 'todo', text: g('offer.proforma', { percent: prepayment.percent }), hint: g('offer.proformaHint') });
  }
  if (prepayment.paidAt === undefined) {
    items.push({ id: 'proformaPaid', state: 'info', text: g('offer.proformaPaidUnknown', { percent: prepayment.percent }) });
  } else {
    items.push(prepayment.paidAt
      ? { id: 'proformaPaid', state: 'ok', text: g('offer.proformaPaid', { date: prepayment.paidAt }) }
      : { id: 'proformaPaid', state: 'todo', text: g('offer.proformaUnpaid', { percent: prepayment.percent }), hint: g('offer.proformaUnpaidHint') });
  }
  return items;
}

/**
 * Szkic of a prepaid ZK: a proforma added early (before the offer) is shown as
 * done, so its importer sees it reached the ZK (2026-10-30). The proforma
 * „to do” rows stay in the Oferta step.
 */
export function getDraftProformaItems(prepayment: OrderGuidePrepaymentFacts | null | undefined): OrderGuideItem[] {
  if (!prepayment || prepayment.proformaNumber == null) return [];
  return [{
    id: 'proforma',
    state: 'ok',
    text: prepayment.proformaNumber ? g('offer.proformaAdded', { number: prepayment.proformaNumber }) : g('offer.proformaAddedNoNumber'),
    hint: g('draft.proformaAddedHint'),
  }];
}

/** Oferta (mockup step 2): Dane zamówienia kompletne, [proforma rows,] Klient potwierdził zamówienie. */
export function getOfferGuideItems(facts: { dataComplete: boolean; offerSent: boolean; prepayment?: OrderGuidePrepaymentFacts | null }): OrderGuideItem[] {
  return [
    {
      id: 'data',
      state: facts.dataComplete ? 'ok' : 'todo',
      text: g('offer.data'),
      hint: g('offer.dataHint'),
    },
    ...(facts.prepayment ? getPrepaymentGuideItems(facts.prepayment) : []),
    {
      id: 'confirmed',
      state: 'todo',
      text: g('offer.confirmed'),
      // The payment question is asked only to readers of customer payments (`paidAt` known).
      hint: facts.prepayment ? g(facts.prepayment.paidAt === undefined ? 'offer.confirmedHintPrepaymentNoPayments' : 'offer.confirmedHintPrepayment')
        : facts.offerSent ? g('offer.confirmedHint') : g('offer.confirmedHintUnsent'),
    },
  ];
}

export interface OrderGuideStockLineFacts {
  name: string;
  unit: string;
  state: 'AVAILABLE' | 'MISSING' | 'ISSUED' | 'UNKNOWN';
  ordered: number | null;
  /** null for the roles the backend hides the stock level from. */
  onHand: number | null;
  missing: number | null;
}

export interface OrderGuideFormatters {
  /** „30” — the amount only. */
  number: (value: number | null) => string;
  /** „30 m²”. */
  quantity: (value: number | null, unit: string) => string;
}

/**
 * Potwierdzone (mockup step 3): the warehouse is the one source (full FIFO),
 * every line says whether it is on the warehouse and, when not, that
 * Valentyna orders it (ZD) and receives it (PZ). Missing goods are information
 * (decision 2026-10-04 pkt 5): the next step stays available.
 */
export function getConfirmedGuideItems(facts: {
  stockLines: OrderGuideStockLineFacts[] | null;
  format: OrderGuideFormatters;
  /** „Prosto z fabryki”: the whole quantity is ordered from the supplier, so the stock level is beside the point. */
  factoryDirect?: boolean;
  /**
   * „Prosto z fabryki”: where its ZD stands (`activeSupplierDraft`) and
   * whether a truck is planned. Without them the row keeps „szkic ZD czeka”.
   */
  factoryZd?: { number: string; ordered: boolean; truckPlanned: boolean } | null;
  /** Lines live ZD buy only in part (decision 2026-10-19-podzial-pozycji-zk-na-dostawcow), in the line unit. */
  partialLines?: Array<{ name: string; unit: string; ordered: number; covered: number; missing: number }>;
}): OrderGuideItem[] {
  const { format } = facts;
  // A line split between suppliers below its quantity: the rest is still to order (both paths).
  const partial = (facts.partialLines ?? []).map((line, index): OrderGuideItem => ({
    id: `zd-partial-${index}`,
    state: 'todo',
    text: g('confirmed.linePartialZd', { name: line.name, covered: format.quantity(line.covered, line.unit),
      ordered: format.quantity(line.ordered, line.unit), missing: format.quantity(line.missing, line.unit) }),
  }));
  if (facts.factoryDirect) {
    const zd = facts.factoryZd;
    // Follow-up v3.16: an ordered ZD (sent to the supplier) no longer „waits as a draft”.
    const factoryText = zd?.ordered
      ? g(zd.truckPlanned ? 'confirmed.factoryOrderedTruck' : 'confirmed.factoryOrdered', { number: zd.number })
      : g('confirmed.factory');
    return [
      { id: 'factory', state: zd?.ordered ? 'ok' : 'info', text: factoryText },
      ...(facts.stockLines ?? []).map((line, index): OrderGuideItem => ({
        id: `line-${index}`,
        state: 'info',
        text: g('confirmed.lineFactory', { name: line.name, ordered: format.quantity(line.ordered, line.unit) }),
      })),
      ...partial,
    ];
  }
  const items: OrderGuideItem[] = [{ id: 'fifo', state: 'info', text: g('confirmed.fifo') }];
  (facts.stockLines ?? []).forEach((line, index) => {
    const id = `line-${index}`;
    if (line.state === 'ISSUED') {
      items.push({ id, state: 'ok', text: g('confirmed.lineIssued', { name: line.name }) });
    } else if (line.state === 'AVAILABLE') {
      items.push({
        id,
        state: 'ok',
        text: line.onHand != null && line.ordered != null
          ? g('confirmed.lineInStock', { name: line.name, available: format.number(Math.min(line.onHand, line.ordered)), ordered: format.quantity(line.ordered, line.unit) })
          : g('confirmed.lineInStockNoLevel', { name: line.name }),
      });
    } else if (line.state === 'MISSING') {
      items.push({
        id,
        state: 'info',
        text: line.onHand != null && line.ordered != null
          ? g('confirmed.lineMissing', { name: line.name, available: format.number(Math.min(line.onHand, line.ordered)), ordered: format.quantity(line.ordered, line.unit) })
          : g('confirmed.lineMissingNoLevel', { name: line.name, missing: format.quantity(line.missing, line.unit) }),
      });
    } else {
      items.push({ id, state: 'info', text: g('confirmed.lineUnknown', { name: line.name }) });
    }
  });
  return [...items, ...partial];
}

/** Help text of the step card (mockup `HELP`); the Oferta text drops „czeka na decyzję” while nothing was sent. */
export function getGuideHelpKey(step: OrderGuideStepKey, offerSent: boolean, factoryDirect = false): string {
  if (step === 'CONFIRMED' && factoryDirect) return g('help.CONFIRMED_FACTORY').key;
  if (step === 'OFFER') return g(offerSent ? 'help.OFFER' : 'help.OFFER_UNSENT').key;
  return g(`help.${step}`).key;
}

/**
 * Label of the main action on the card: the mockup's names where the
 * meaning is the same („Klient potwierdził”, „Rozpocznij realizację”), the
 * action bar's label everywhere else („Stwórz ofertę” stays — the status
 * „Oferta wysłana” is set only by sending the offer, FF.18).
 */
export function getGuidePrimaryLabelKey(action: OrderStepPrimaryAction): string {
  if (action.kind === 'confirm') return g('actions.customerConfirmed').key;
  if (action.kind === 'status' && action.status === 'IN_FULFILLMENT') return g('actions.startFulfillment').key;
  return action.labelKey;
}
